// Swatch images shown on Product Display page
import type { Dispatch, SetStateAction } from 'react'

import Image from 'next/image'

import isEqual from 'lodash/isEqual'
import kebabCase from 'lodash/kebabCase'

import type {
	Image as ImageType,
	ProductOption,
	Product as ProductType,
	ProductVariant
} from '@lib/types'

type Props = {
	product: ProductType
	selectedOptions: ProductVariant['selectedOptions']
	setSelectedOptions: Dispatch<SetStateAction<ProductVariant['selectedOptions']>>
	setSelectedVariant: Dispatch<SetStateAction<ProductVariant>>
	setCurrentImageHandler: (image: ImageType) => void
}

export default function Options({
	product,
	selectedOptions,
	setSelectedOptions,
	setSelectedVariant,
	setCurrentImageHandler
}: Props) {
	// Select variant on click
	const selectVariant = (index: number, optionName: string, option: string) => {
		// Set the choosen options based on their index
		const newChoosenOptions = [...selectedOptions]
		newChoosenOptions[index] = { name: optionName, value: option }
		setSelectedOptions(newChoosenOptions)

		// Look for the variant that matches the choosen options
		product?.variants.forEach((variant) => {
			if (isEqual(variant.selectedOptions, newChoosenOptions)) {
				setSelectedVariant(variant)
			}
		})
	}

	const variantDetails = (productOption: string) =>
		product.variants.find((variant) => productOption === variant.name)

	return (
		<>
			{/* Show options selection nav if we have more than one variant. */}
			{product?.variants.length > 1 &&
				product.options.map((optionSet: ProductOption, index: number) => {
					// Filter options to not include samples

					const optionsThatAreNotSamples = optionSet.values.filter((option) => {
						const details = variantDetails(option)
						const priceAmount = Number(details?.price?.amount)
						const isZeroPrice = Number.isFinite(priceAmount) && priceAmount <= 0
						return Boolean(details) && !isZeroPrice
					})
					return !optionsThatAreNotSamples.length ? null : (
						<div key={optionSet.id} className="mb-10">
							{/* Name of option set */}
							<p className="mb-3 text-xs">
								<span className="font-medium">{optionSet.name}</span>: {selectedOptions?.[0]?.value}
							</p>
							{/* Options within option set */}
							<nav className="flex flex-wrap items-center gap-x-1 gap-y-2">
								{optionsThatAreNotSamples.map((option) => (
									<button
										key={kebabCase(option)}
										onClick={() => {
											selectVariant(index, optionSet.name, option)
											setCurrentImageHandler(variantDetails(option)?.image as ImageType)
										}}
										className={`
										rounded-full border transition-colors duration-300 ${
											selectedOptions?.[index]?.value === option
												? 'border-gray-350'
												: 'border-transparent'
										}
									`}
										title={option}
										type="button"
									>
										<div className="relative overflow-hidden border-4 border-white rounded-full w-11 h-11">
											{variantDetails(option)?.swatch?.image?.url ? (
												<Image
													layout="fill"
													className="bg-cover"
													objectFit="cover"
													src={variantDetails(option)?.swatch?.image?.url || ''}
													alt={`${product?.brand ?? ''} ${product.name} ${option} swatch`}
												/>
											) : (
												<div className="flex items-center justify-start h-full px-2 text-xs font-medium text-gray-500 bg-gray-200 rounded-full whitespace-nowrap">
													<span className="-mb-0.5">{option}</span>
												</div>
											)}
										</div>
									</button>
								))}
							</nav>
						</div>
					)
				})}
		</>
	)
}
